import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Botao, Campo, Cartao, classesEntrada, Esqueleto, TituloSecao } from '@/componentes/ui'
import { supabase } from '@/lib/supabase'
import { cn, mensagemDeErro } from '@/lib/utilitarios'
import { CATEGORIAS_VIDEO, ROTULO_CATEGORIA_VIDEO, type CategoriaVideo } from '@/tipos/dominio'
import type { Video } from '@/tipos/tabelas'
import { CHAVES_ADMIN, extrairIdYouTube, useProgramaAtual } from './comum'

/**
 * Vídeos das reprogramações e dos exercícios (seções 6 e 7).
 *
 * Destrava dois dos itens que a especificação deixou em aberto na seção
 * 17. O app não hospeda vídeo: guarda só o ID do YouTube.
 */
export function SecaoVideos() {
  const { data: programa } = useProgramaAtual()
  const clienteQuery = useQueryClient()

  const [categoria, setCategoria] = useState<CategoriaVideo>('reprogramacao_manha')
  const [titulo, setTitulo] = useState('')
  const [link, setLink] = useState('')
  const [diaNumero, setDiaNumero] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  const idExtraido = extrairIdYouTube(link)

  const { data: videos = [], isLoading } = useQuery({
    queryKey: CHAVES_ADMIN.videos(programa?.id ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('programa_id', programa?.id ?? '')
        .order('categoria')
        .order('ordem')

      if (error) throw error
      return data as Video[]
    },
    enabled: Boolean(programa),
  })

  const adicionar = useMutation({
    mutationFn: async () => {
      if (!programa) throw new Error('Nenhum programa ativo.')
      if (!idExtraido) throw new Error('Não reconheci o link do YouTube.')

      // Reprogramações são amarradas a um dia; exercícios ficam soltos.
      let diaProgramaId: string | null = null
      const numero = Number(diaNumero)
      if (numero >= 1 && numero <= programa.duracao_dias) {
        const { data } = await supabase
          .from('dias_programa')
          .select('id')
          .eq('programa_id', programa.id)
          .eq('numero', numero)
          .maybeSingle()
        diaProgramaId = (data as { id: string } | null)?.id ?? null
      }

      const mesmaCategoria = videos.filter((v) => v.categoria === categoria)

      const { error } = await supabase.from('videos').insert({
        programa_id: programa.id,
        categoria,
        titulo: titulo.trim(),
        youtube_id: idExtraido,
        dia_programa_id: diaProgramaId,
        ordem: mesmaCategoria.length,
      })
      if (error) throw error
    },
    onSuccess: () => {
      setTitulo('')
      setLink('')
      setDiaNumero('')
      setErro(null)
      void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.videos(programa?.id ?? '') })
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  })

  const remover = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('videos').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () =>
      void clienteQuery.invalidateQueries({ queryKey: CHAVES_ADMIN.videos(programa?.id ?? '') }),
  })

  const ehReprogramacao = categoria.startsWith('reprogramacao')

  return (
    <div className="flex flex-col gap-6">
      <section>
        <TituloSecao>Adicionar vídeo</TituloSecao>
        <Cartao className="flex flex-col gap-4">
          <fieldset>
            <legend className="mb-2 font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
              Categoria
            </legend>
            <div className="flex flex-wrap gap-2">
              {CATEGORIAS_VIDEO.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategoria(cat)}
                  aria-pressed={categoria === cat}
                  className={cn(
                    'h-10 rounded-full px-3 text-sm font-medium transition-colors',
                    categoria === cat
                      ? 'bg-coral text-white'
                      : 'bg-[var(--fundo)] text-[var(--tinta-suave)]',
                  )}
                >
                  {ROTULO_CATEGORIA_VIDEO[cat]}
                </button>
              ))}
            </div>
          </fieldset>

          <Campo rotulo="Título">
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Reprogramação da manhã — Dia 1"
              className={classesEntrada}
            />
          </Campo>

          <Campo
            rotulo="Link do YouTube"
            dica={
              idExtraido
                ? `Reconhecido: ${idExtraido}`
                : 'Cole o link inteiro. Aceita youtube.com, youtu.be, shorts e embed.'
            }
            erro={link && !idExtraido ? 'Não reconheci um vídeo do YouTube nesse link.' : undefined}
          >
            <input
              type="text"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className={classesEntrada}
            />
          </Campo>

          {ehReprogramacao && (
            <Campo
              rotulo="Dia do programa"
              dica="Opcional. Amarra este vídeo a um dia específico dos 28."
            >
              <input
                type="number"
                min={1}
                max={programa?.duracao_dias ?? 28}
                value={diaNumero}
                onChange={(e) => setDiaNumero(e.target.value)}
                placeholder="1"
                className={classesEntrada}
              />
            </Campo>
          )}

          {erro && (
            <p role="alert" className="text-sm text-pink">
              {erro}
            </p>
          )}

          <Botao
            tamanho="bloco"
            disabled={!titulo.trim() || !idExtraido}
            carregando={adicionar.isPending}
            onClick={() => adicionar.mutate()}
          >
            Adicionar vídeo
          </Botao>
        </Cartao>
      </section>

      {isLoading ? (
        <Esqueleto className="h-40" />
      ) : (
        CATEGORIAS_VIDEO.map((cat) => {
          const daCategoria = videos.filter((v) => v.categoria === cat)
          if (daCategoria.length === 0) return null

          return (
            <section key={cat}>
              <TituloSecao>{ROTULO_CATEGORIA_VIDEO[cat]}</TituloSecao>
              <div className="flex flex-col gap-2">
                {daCategoria.map((video) => (
                  <Cartao key={video.id} className="flex items-center gap-3 p-3">
                    <img
                      src={`https://i.ytimg.com/vi/${video.youtube_id}/default.jpg`}
                      alt=""
                      loading="lazy"
                      className="h-12 w-16 shrink-0 rounded-lg object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-medium">{video.titulo}</p>
                      <p className="truncate text-xs text-[var(--tinta-apagada)]">
                        {video.youtube_id}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => remover.mutate(video.id)}
                      aria-label={`Remover ${video.titulo}`}
                      className="shrink-0 px-2 text-[var(--tinta-apagada)]"
                    >
                      ✕
                    </button>
                  </Cartao>
                ))}
              </div>
            </section>
          )
        })
      )}
    </div>
  )
}
